<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
    import { ServicesRepository } from '../../modules/posApi/services/services.repository'
    import { ServicesService } from '../../modules/posApi/services/services.service'
    import { Service } from '../../modules/posApi/services/service'
    import type { CreateServiceDto } from '../../modules/posApi/services/dto/create-service.dto'
    import type { UpdateServiceDto } from '../../modules/posApi/services/dto/update-service.dto'
    import ObPosServicesItem from './ob-pos-services-item.svelte'
    import ObStep from './ob-step.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    const dispatch = createEventDispatcher()
    let min = 0
    let total = 0
    let services = []
    let show = false
    let loaded = false
    let status = ''
    const rep = new ServicesRepository()
    const service = new ServicesService(rep)

    // a lista começa no que já existe: não dá para ter menos serviços do que os já cadastrados
    $: totalOptions = Array.from({ length: 100 }, (_, index) => ({ value: index + min, label: String(index + min) }))

    function totalChange() {
        while (services.length != total) {
            if (services.length > total) {
                services.pop()
                continue
            }
            if (services.length < total) {
                let s = new Service()
                services.push(s)
            }
        }
        services = services
    }

    function onBack() {
        if (services.length > 0) {
            if (!confirm('As alterações dessa tela serão perdidas ao voltar. Deseja mesmo voltar?')) return false
        }
        dispatch('back')
    }

    async function onNext() {
        if (show) return
        if (total === 0) {
            window.error(['Informe ao menos 1 serviço prestado.', 'Você deve informar ao menos 1 serviço agora e poderá adicionar outros depois.'])
            return
        }
        if (await saveEntity()) {
            dispatch('next')
        }
    }

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        try {
            show = true
            let result = await service.getAll()
            min = result.length
            total = result.length
            services = result
            loaded = true
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function createEntity(entity) {
        try {
            if (entity.duration && entity.duration > 999) entity.duration = 999
            const dto: CreateServiceDto = {
                comissionFixed: 0,
                comissionPercentage: 0,
                comissionCosts: 0,
                comissionFixedAttendant: 0,
                comissionPercentageAttendant: 0,
                costs: 0,
                comissionCostsAttendant: 0,
                duration: entity.duration,
                name: entity.name,
                price: entity.price,
                consulting: entity.consulting,
                balance: !entity.consulting,
                type: 'service',
                stock: 0,
                description: '',
                public: true,
                scheduleInterval: 0,
                schedulers: [],
                schedulersOnline: [],
                blockDuplicity: true,
                needResource: false,
                services: undefined,
                expirationDays: undefined,
                documents: [],
            }
            entity = await service.create(dto)
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
        }
    }

    async function updateEntity(entity) {
        try {
            if (entity.duration && entity.duration > 999) entity.duration = 999
            const dto: UpdateServiceDto = {
                comissionFixed: entity.comissionFixed,
                comissionPercentage: entity.comissionPercentage,
                duration: entity.duration,
                name: entity.name,
                price: entity.price,
                consulting: entity.consulting,
                services: undefined,
            }
            entity = await service.update(entity.serviceId, dto)
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
        }
    }

    function validateAll() {
        for (let entity of services) {
            if (!validateFields(entity)) return false
        }
        return true
    }

    async function saveEntity() {
        services = services.filter((el) => !!el)
        if (!validateAll()) return false
        let count = 0
        try {
            show = true
            for (let entity of services) {
                count++
                status = 'Salvando ' + count + ' de ' + services.length
                if (entity.serviceId) {
                    if (!(await updateEntity(entity))) return false
                } else {
                    if (!(await createEntity(entity))) return false
                }
            }
        } catch (error) {
        } finally {
            show = false
        }

        return true
    }

    function validateFields(entity) {
        if (!entity.name) {
            window.error('Informe o nome do item')
            return false
        }
        if (!entity.duration && entity.duration !== 0) {
            window.error('Informe uma estimativa de tempo para a realização do serviço (ou 0 para produtos)')
            return false
        }
        if (!entity.price && entity.price !== 0) {
            window.error('Informe o valor do serviço')
            return false
        }
        return true
    }
</script>

<ObStep
    title="Serviços / Itens"
    label="Confirme os serviços oferecidos. Você também pode informar itens que devam entrar na agenda ou ponto de vendas (ex.: gorjeta)"
>
    <svelte:fragment slot="acts">
        <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={onBack} />
        <Zbutton color="primary" icon={faAngleRight} label="Avançar" loading={show} disabled={show} on:click={onNext} />
    </svelte:fragment>

    <ZContainer gapRow="8px" gapCol="8px">
        <Zinput size={4} sizeM={12} label="Quantidade de serviços" type="select" options={totalOptions} bind:value={total} on:change={totalChange} />
    </ZContainer>

    {#each services as service}
        <ObPosServicesItem bind:service />
    {/each}

    <div class="mt-3 text-muted">
        <b>Quantidade de serviços:</b><br />
        Representa a quantidade de serviços prestados por você.<br />
        Não é necessário adicionar tudo agora, mas recomendamos que cadastre alguns para experimentar o Zandu.
    </div>
    {#if services.length > 0}
        <div class="mt-3 text-muted">
            Você também pode cadastrar aqui itens como:<br />
            <b>Gorjeta:</b> basta informar duração 0, valor 0 e comissão 100%<br />
            <b>Refrigerante:</b> basta informar duração 0, valor e comissão<br />
        </div>
        <div class="mt-3 text-muted">
            <b>Duração:</b><br />
            É o tempo estimado para a realização do serviço. Este tempo não precisa ser exato, basta um valor aproximado.<br />
            Caso seja um produto informe 0.
        </div>
    {/if}
</ObStep>

<LoadSpinner {show}>{status}</LoadSpinner>
